// MCP servers (FD §12): the tool servers agents can use during a run.
//
// Registered here: a URL (Streamable HTTP or the older SSE) or a command
// that starts inside the run's sandbox, with an optional secret (stored
// encrypted, never shown again) sent as a header or environment variable.
// A URL server is checked right away: the handshake plus its tool list.
//
// Who may use a server is set on Roles (which servers the role allows) and
// Agents (which of those the agent gets); this page shows who uses it.

import { Fragment, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { McpService } from '@goblic/shared'
import {
  agentsApi,
  ApiError,
  mcpServicesApi,
  type McpServiceInput,
} from '../lib/api-client'
import { useWorkspaceId } from '../lib/auth'
import { useDialogs } from '../components/dialogs'
import { StatusBadge } from '../components/status-badge'
import {
  Dialog,
  EmptyState,
  ErrorNote,
  Field,
  LoadingRows,
  Notice,
  PageHeader,
} from '../components/ui'
import { formatRelative } from '../lib/format'

type Transport = McpServiceInput['transportType']

const TRANSPORTS: { value: Transport; label: string; text: string }[] = [
  {
    value: 'http',
    label: 'URL',
    text: 'A remote server over Streamable HTTP, the current MCP standard.',
  },
  {
    value: 'sse',
    label: 'URL (SSE)',
    text: 'A remote server on the older HTTP + SSE transport. Codex can’t use these.',
  },
  {
    value: 'stdio',
    label: 'Command',
    text: 'A server that starts inside each run’s sandbox, such as an npx package.',
  },
]

const TRANSPORT_LABEL: Record<string, string> = {
  http: 'URL',
  sse: 'URL (SSE)',
  stdio: 'Command',
  websocket: 'WebSocket',
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

function loginText(service: McpService): string {
  if (!service.auth) return 'No login'
  return service.auth.kind === 'header'
    ? `Secret in ${service.auth.header} header`
    : `Secret in $${service.auth.name}`
}

export function McpServicesPage() {
  const workspaceId = useWorkspaceId()
  const queryClient = useQueryClient()
  const { confirm, alert } = useDialogs()
  const [dialog, setDialog] = useState<
    { mode: 'add' } | { mode: 'edit'; service: McpService } | null
  >(null)
  const [openTools, setOpenTools] = useState<string | null>(null)

  const servicesQuery = useQuery({
    queryKey: ['mcp-services', workspaceId],
    queryFn: () => mcpServicesApi.list({ workspaceId }),
    enabled: workspaceId !== '',
  })
  const services = servicesQuery.data?.items ?? []

  const agentsQuery = useQuery({
    queryKey: ['agents', workspaceId],
    queryFn: () => agentsApi.list({ workspaceId }),
    enabled: workspaceId !== '',
  })
  const agents = agentsQuery.data?.items ?? []

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['mcp-services', workspaceId] })

  const check = useMutation({
    mutationFn: (id: string) => mcpServicesApi.check(id),
    onSettled: refresh,
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t check the server',
        message: errorText(error, ''),
      }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => mcpServicesApi.remove(id),
    onSuccess: refresh,
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t remove the server',
        message: errorText(error, ''),
      }),
  })

  async function handleRemove(service: McpService): Promise<void> {
    const users = agents.filter((agent) =>
      agent.mcpServiceIds.includes(service.id)
    )
    const ok = await confirm({
      kind: 'delete',
      title: `Remove ${service.name}?`,
      message:
        users.length > 0
          ? `${users.length === 1 ? 'One agent uses' : `${users.length} agents use`} it; their runs go on without it. Its stored secret is revoked.`
          : 'No agent uses it. Its stored secret is revoked.',
      confirmLabel: 'Remove',
    })
    if (ok) remove.mutate(service.id)
  }

  return (
    <div>
      <PageHeader
        title="MCP servers"
        description="Tool servers agents can use during a run, such as GitHub, Linear or a database. An agent gets a server when its role allows it and the agent has it ticked."
        actions={
          <button
            type="button"
            className="btn-primary"
            onClick={() => setDialog({ mode: 'add' })}
          >
            Add MCP server
          </button>
        }
      />

      <div className="panel overflow-x-auto">
        {servicesQuery.isLoading ? (
          <LoadingRows />
        ) : servicesQuery.isError ? (
          <ErrorNote error={servicesQuery.error} what="MCP servers" />
        ) : services.length === 0 ? (
          <EmptyState
            title="No MCP servers yet"
            art="setup"
            action={
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setDialog({ mode: 'add' })}
              >
                Add an MCP server
              </button>
            }
          >
            Add a server by its URL or start command. Then allow it on a role
            and tick it on the agents that should use it.
          </EmptyState>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Server</th>
                <th>Connection</th>
                <th>Status</th>
                <th className="text-right">Tools</th>
                <th>Used by</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {services.map((service) => {
                const users = agents.filter((agent) =>
                  agent.mcpServiceIds.includes(service.id)
                )
                const toolsOpen = openTools === service.id
                const checking =
                  check.isPending && check.variables === service.id
                return (
                  <Fragment key={service.id}>
                    <tr>
                      <td>
                        <span className="block font-medium text-ink">
                          {service.name}
                        </span>
                        {service.description ? (
                          <span className="block max-w-md truncate text-sm text-ink-3">
                            {service.description}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <span className="block">
                          {TRANSPORT_LABEL[service.transportType] ??
                            service.transportType}
                          <span className="text-ink-3">
                            {' '}
                            · {loginText(service)}
                          </span>
                        </span>
                        <span
                          className="block max-w-xs truncate font-mono text-sm text-ink-3"
                          title={service.endpointOrCommand}
                        >
                          {service.endpointOrCommand}
                        </span>
                      </td>
                      <td>
                        {service.transportType === 'stdio' ? (
                          <span className="text-sm text-ink-3">
                            Starts in the run
                          </span>
                        ) : (
                          <>
                            <StatusBadge status={service.healthStatus} />
                            {service.lastHealthError ? (
                              <span className="mt-1 block max-w-xs text-sm text-danger">
                                {service.lastHealthError}
                              </span>
                            ) : service.lastHealthCheckAt ? (
                              <span className="block text-sm text-ink-3">
                                {formatRelative(service.lastHealthCheckAt)}
                              </span>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td className="text-right tabular-nums">
                        {service.availableTools.length > 0 ? (
                          <button
                            type="button"
                            className="link"
                            aria-expanded={toolsOpen}
                            onClick={() =>
                              setOpenTools(toolsOpen ? null : service.id)
                            }
                          >
                            {service.availableTools.length}
                          </button>
                        ) : (
                          <span className="text-ink-3">–</span>
                        )}
                      </td>
                      <td className="max-w-[14rem]">
                        {users.length === 0 ? (
                          <span className="text-ink-3">No agents</span>
                        ) : (
                          <span
                            className="block truncate"
                            title={users.map((agent) => agent.name).join(', ')}
                          >
                            {users.map((agent) => agent.name).join(', ')}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex justify-end gap-1">
                          {service.transportType !== 'stdio' ? (
                            <button
                              type="button"
                              className="btn-ghost btn-sm"
                              onClick={() => check.mutate(service.id)}
                              disabled={checking}
                            >
                              {checking ? 'Checking…' : 'Check'}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="btn-ghost btn-sm"
                            onClick={() => setDialog({ mode: 'edit', service })}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn-ghost btn-sm text-danger"
                            onClick={() => void handleRemove(service)}
                            disabled={remove.isPending}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                    {toolsOpen ? (
                      <tr>
                        <td colSpan={6} className="bg-sunken">
                          <ul className="grid gap-x-6 gap-y-2 py-1 sm:grid-cols-2">
                            {service.availableTools.map((tool) => (
                              <li key={tool.name} className="min-w-0">
                                <span className="block font-mono text-sm text-ink">
                                  {tool.name}
                                </span>
                                {tool.description ? (
                                  <span className="block truncate text-sm text-ink-3">
                                    {tool.description}
                                  </span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-sm text-ink-3">
        To give an agent a server: allow it on the agent’s role under{' '}
        <Link to="/roles" className="link">
          Roles
        </Link>
        , then tick it on the agent in the project’s Agents section. Runs can reach a URL server only when it’s reachable from the
        internet.
      </p>

      <Dialog
        open={dialog != null}
        onClose={() => setDialog(null)}
        title={
          dialog?.mode === 'edit'
            ? `Edit ${dialog.service.name}`
            : 'Add an MCP server'
        }
        description="A secret is stored encrypted and never shown again."
        art="/goblins/dialog-connect.webp"
        size="md"
      >
        {dialog ? (
          <McpServerForm
            key={dialog.mode === 'edit' ? dialog.service.id : 'add'}
            workspaceId={workspaceId}
            existing={dialog.mode === 'edit' ? dialog.service : null}
            onDone={(saved) => {
              setDialog(null)
              void refresh()
              // A URL server is checked right away, so its status and tools show.
              if (saved.transportType !== 'stdio') check.mutate(saved.id)
            }}
          />
        ) : null}
      </Dialog>
    </div>
  )
}

/**
 * Add a server, or edit one. The login is a secret sent as a header (URL
 * servers) or an environment variable (command servers); when editing, an
 * empty secret keeps the stored one.
 */
function McpServerForm({
  workspaceId,
  existing,
  onDone,
}: {
  workspaceId: string
  existing: McpService | null
  onDone: (saved: McpService) => void
}) {
  const [transport, setTransport] = useState<Transport>(
    (existing?.transportType as Transport | undefined) ?? 'http'
  )
  const [name, setName] = useState(existing?.name ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')
  const [endpoint, setEndpoint] = useState(existing?.endpointOrCommand ?? '')
  const [useLogin, setUseLogin] = useState(existing?.auth != null)
  const [header, setHeader] = useState(
    existing?.auth?.kind === 'header' ? existing.auth.header : 'Authorization'
  )
  const [prefix, setPrefix] = useState(
    existing?.auth?.kind === 'header' ? existing.auth.prefix : 'Bearer '
  )
  const [envName, setEnvName] = useState(
    existing?.auth?.kind === 'env' ? existing.auth.name : ''
  )
  const [secret, setSecret] = useState('')

  const isCommand = transport === 'stdio'
  // A stored secret only carries over when the login stays the same kind.
  const loginKind = isCommand ? 'env' : 'header'
  const keepsSecret =
    existing?.hasSecret === true && existing.auth?.kind === loginKind

  const save = useMutation({
    mutationFn: () => {
      const auth: McpServiceInput['auth'] = !useLogin
        ? null
        : isCommand
          ? { kind: 'env', name: envName.trim() }
          : { kind: 'header', header: header.trim(), prefix }
      const body: McpServiceInput = {
        name: name.trim(),
        description: description.trim() || null,
        transportType: transport,
        endpointOrCommand: endpoint.trim(),
        auth,
        ...(useLogin && secret.trim() ? { secret: secret.trim() } : {}),
      }
      return existing
        ? mcpServicesApi.update(existing.id, body)
        : mcpServicesApi.create({ ...body, workspaceId })
    },
    onSuccess: onDone,
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    save.mutate()
  }

  const loginComplete =
    !useLogin ||
    ((isCommand ? envName.trim() !== '' : header.trim() !== '') &&
      (secret.trim() !== '' || keepsSecret))
  const canSave = name.trim() !== '' && endpoint.trim() !== '' && loginComplete

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <fieldset>
        <legend className="mb-2 text-base font-medium text-ink">
          How is it reached?
        </legend>
        <div className="space-y-2">
          {TRANSPORTS.map((option) => {
            const selected = option.value === transport
            return (
              <label
                key={option.value}
                className={`flex cursor-pointer gap-3 rounded-control border px-4 py-2.5 transition-colors ${
                  selected
                    ? 'border-action bg-action/5'
                    : 'border-line hover:border-line-strong'
                }`}
              >
                <input
                  type="radio"
                  name="mcp-transport"
                  className="mt-1"
                  checked={selected}
                  onChange={() => setTransport(option.value)}
                />
                <span>
                  <span className="block text-base font-medium text-ink">
                    {option.label}
                  </span>
                  <span className="block text-sm text-ink-3">
                    {option.text}
                  </span>
                </span>
              </label>
            )
          })}
        </div>
      </fieldset>

      <Field
        label="Name"
        htmlFor="mcp-name"
        hint="Agents see this name; their tools are named after it."
      >
        <input
          id="mcp-name"
          className="field-input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="GitHub"
          maxLength={80}
          required
        />
      </Field>

      <Field
        label="What it’s for"
        htmlFor="mcp-description"
        hint="Optional. Passed to the agent, so say when to use it."
      >
        <input
          id="mcp-description"
          className="field-input"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Issues and pull requests of our repositories"
        />
      </Field>

      {isCommand ? (
        <Field
          label="Command"
          htmlFor="mcp-endpoint"
          hint="Runs in the sandbox of each run, in a shell. Packages from npm (npx) or PyPI (uvx) are downloaded when the run starts."
        >
          <input
            id="mcp-endpoint"
            className="field-input font-mono"
            value={endpoint}
            onChange={(event) => setEndpoint(event.target.value)}
            placeholder="npx -y @modelcontextprotocol/server-github"
            autoCapitalize="none"
            spellCheck={false}
            required
          />
        </Field>
      ) : (
        <Field
          label="URL"
          htmlFor="mcp-endpoint"
          hint="The server’s MCP endpoint, often ending in /mcp (or /sse for SSE servers)."
        >
          <input
            id="mcp-endpoint"
            className="field-input font-mono"
            value={endpoint}
            onChange={(event) => setEndpoint(event.target.value)}
            placeholder="https://mcp.example.com/mcp"
            autoCapitalize="none"
            spellCheck={false}
            required
          />
        </Field>
      )}

      <fieldset>
        <legend className="mb-2 text-base font-medium text-ink">Login</legend>
        <div className="flex gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="mcp-login"
              checked={!useLogin}
              onChange={() => setUseLogin(false)}
            />
            None
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="mcp-login"
              checked={useLogin}
              onChange={() => setUseLogin(true)}
            />
            A secret, such as a token or API key
          </label>
        </div>
      </fieldset>

      {useLogin ? (
        <>
          {isCommand ? (
            <Field
              label="Environment variable"
              htmlFor="mcp-env"
              hint="The command gets the secret in this variable."
            >
              <input
                id="mcp-env"
                className="field-input font-mono"
                value={envName}
                onChange={(event) => setEnvName(event.target.value)}
                placeholder="GITHUB_PERSONAL_ACCESS_TOKEN"
                autoCapitalize="none"
                spellCheck={false}
              />
            </Field>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Header" htmlFor="mcp-header">
                <input
                  id="mcp-header"
                  className="field-input font-mono"
                  value={header}
                  onChange={(event) => setHeader(event.target.value)}
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </Field>
              <Field
                label="Before the secret"
                htmlFor="mcp-prefix"
                hint="Usually “Bearer ”; empty for a bare key."
              >
                <input
                  id="mcp-prefix"
                  className="field-input font-mono"
                  value={prefix}
                  onChange={(event) => setPrefix(event.target.value)}
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </Field>
            </div>
          )}
          <Field
            label={keepsSecret ? 'New secret' : 'Secret'}
            htmlFor="mcp-secret"
            hint={keepsSecret ? 'Leave empty to keep the stored secret.' : undefined}
          >
            <input
              id="mcp-secret"
              type="password"
              className="field-input font-mono"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
            />
          </Field>
        </>
      ) : null}

      {save.isError ? (
        <Notice tone="error">
          {errorText(save.error, 'Couldn’t save the MCP server.')}
        </Notice>
      ) : null}

      <div className="flex justify-end border-t border-line pt-4">
        <button
          type="submit"
          className="btn-primary"
          disabled={save.isPending || !canSave}
        >
          {save.isPending ? 'Saving…' : existing ? 'Save' : 'Add server'}
        </button>
      </div>
    </form>
  )
}
